/**
 * Pure helpers for the phone-verification card. Kept out of the component so
 * they are testable without a DOM, which is how this codebase tests UI logic.
 */

/** What to say for each refusal the API names. Anything unlisted falls back to
 *  the server's own message, which is already written for a person. */
export const COPY_BY_CODE: Readonly<Record<string, string>> = {
  sms_unavailable:
    'Phone verification is not available yet. Nothing was sent — your Account Executive can still reach you by email.',
  country_code_required: 'Include the country code, for example +52 or +1.',
  unsupported_country: 'We can only reach numbers in Mexico, the United States and Canada.',
  expired: 'That code expired. Ask for a new one.',
  too_many_attempts: 'Too many wrong codes. Ask for a new one.',
  no_code: 'Ask for a code first.',
  mfa_step_up_required:
    'Changing your number takes a code from your authenticator app. Enter it below and send again.',
  mfa_not_enrolled:
    'Changing your number needs an authenticator app. Set one up in the section above first.',
  mfa_invalid_code: 'That authenticator code is not right. Try the current one.',
};

/** Refusals after which the live code is gone, so retyping it is pointless and
 *  the card must go back to asking for a number. `wrong_code` is deliberately
 *  absent: the holder still has attempts left on the same code. */
const CODE_IS_DEAD = new Set(['too_many_attempts', 'expired', 'no_code']);

export function copyForRefusal(code: string | null, fallback: string): string {
  return (code && COPY_BY_CODE[code]) || fallback;
}

export function mustAskForANewCode(code: string | null): boolean {
  return code !== null && CODE_IS_DEAD.has(code);
}

/** 125 → "2:05". Display only: the server owns the expiry. */
export function clock(seconds: number): string {
  const safe = Math.max(0, Math.floor(seconds));
  return `${Math.floor(safe / 60)}:${String(safe % 60).padStart(2, '0')}`;
}

/** Keep digits only, capped at the code length, so a pasted "418 923" works. */
export function sanitizeCode(raw: string, length = 6): string {
  return raw.replace(/\D/g, '').slice(0, length);
}

/** Refusals that mean "this is a CHANGE of number — show the authenticator field". */
const NEEDS_AUTHENTICATOR = new Set(['mfa_step_up_required', 'mfa_invalid_code', 'mfa_code_replayed']);

export function needsAuthenticatorCode(code: string | null): boolean {
  return code !== null && NEEDS_AUTHENTICATOR.has(code);
}
